/**
 * Per-org branding for exports — fetched once, then drawn into Excel (exceljs) and
 * PDF (jsPDF) document headers. The org's logo, brand colour, name, ABN, address,
 * phone, email, website and NDIS reg # form the letterhead at the top of every export.
 *
 * Source of truth: GET /api/organizations/branding (own org). See backend
 * utils/orgBranding.js + routes/organizations.js.
 */
import api from './api';

let _cache = null;
let _inflight = null;

// Fetch the caller's org branding once and cache it for the session.
export async function getBranding() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = api.get('/organizations/branding')
      .then(r => { _cache = r.data || {}; return _cache; })
      .catch(() => ({}))
      .finally(() => { _inflight = null; });
  }
  return _inflight;
}

export function clearBrandingCache() { _cache = null; }

const DEFAULT_HEX = '4F46E5';

// Brand colour as 6-hex WITHOUT '#'. Falls back to indigo.
export function brandHex(branding, fallback = DEFAULT_HEX) {
  const c = branding && branding.brandColor;
  if (!c || typeof c !== 'string') return fallback;
  const m = c.trim().replace(/^#/, '');
  return /^[0-9a-fA-F]{6}$/.test(m) ? m.toUpperCase() : fallback;
}

// Brand colour as an {r,g,b} triple (for jsPDF setFillColor).
export function brandRgb(branding, fallback = DEFAULT_HEX) {
  const h = brandHex(branding, fallback);
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

// Parse the logo data URL → { extension, base64, w, h } usable by exceljs.
// exceljs embeds png/jpeg/gif only (NOT webp) — returns null for unsupported types.
export function excelLogo(branding) {
  const d = branding && branding.logoDataUrl;
  if (!d) return null;
  const m = /^data:image\/([a-z]+);base64,(.+)$/i.exec(d);
  if (!m) return null;
  let ext = m[1].toLowerCase();
  if (ext === 'jpg') ext = 'jpeg';
  if (!['png', 'jpeg', 'gif'].includes(ext)) return null;
  const dims = branding.logoDims || {};
  return { extension: ext, base64: m[2], w: dims.w || 0, h: dims.h || 0 };
}

// Logo data URL + jsPDF format ('PNG'|'JPEG'|'WEBP') — jsPDF supports webp too.
export function pdfLogo(branding) {
  const d = branding && branding.logoDataUrl;
  if (!d) return null;
  const m = /^data:image\/([a-z]+);base64,/i.exec(d);
  if (!m) return null;
  let ext = m[1].toLowerCase();
  const fmt = ext === 'png' ? 'PNG' : (ext === 'jpg' || ext === 'jpeg') ? 'JPEG' : ext === 'webp' ? 'WEBP' : null;
  if (!fmt) return null;
  const dims = branding.logoDims || {};
  return { dataUrl: d, format: fmt, w: dims.w || 0, h: dims.h || 0 };
}

// Two letterhead detail lines from the org profile (empty strings filtered out).
export function brandingLines(branding) {
  if (!branding) return [];
  const sep = '   ·   ';
  const l1 = [branding.abn && `ABN: ${branding.abn}`, branding.address].filter(Boolean).join(sep);
  const l2 = [
    branding.phone, branding.email, branding.website,
    branding.ndisRegistrationNumber && `NDIS: ${branding.ndisRegistrationNumber}`,
  ].filter(Boolean).join(sep);
  return [l1, l2].filter(Boolean);
}

// Scale a logo to fit a max height/width box, keeping aspect ratio. px.
export function logoBox(img, maxH = 40, maxW = 150) {
  if (!img || !img.w || !img.h) return { width: maxW, height: maxH };
  let h = maxH, w = (img.w / img.h) * h;
  if (w > maxW) { w = maxW; h = (img.h / img.w) * w; }
  return { width: Math.round(w), height: Math.round(h) };
}

/**
 * Draw the branded letterhead into the TOP of an exceljs worksheet.
 *   rows 1: brand-colour band with the org name (logo floats top-left)
 *   rows 2-3: detail lines (ABN/address ; phone/email/website/NDIS)
 *   row 4: report title
 *   row 5: meta (generated date + any extra)
 *   row 6: spacer
 * Returns the row number where the caller should place its column-header row (7).
 */
export function renderExcelHeader(wb, ws, { branding, reportTitle, metaText, lastCol }) {
  const hex = brandHex(branding);
  const name = (branding && branding.name) || 'Rostery';
  const lines = brandingLines(branding).filter(Boolean);
  const hasLogo = !!(branding && branding.logoDataUrl);

  let r = 0;
  const merge = (value, font, fillHex, height, align, border) => {
    r += 1;
    ws.mergeCells(`A${r}:${lastCol}${r}`);
    const c = ws.getCell(`A${r}`);
    c.value = value;
    c.font = font;
    if (fillHex) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + fillHex } };
    c.alignment = { horizontal: align || 'left', vertical: 'middle', indent: 1 };
    if (border) c.border = border;
    ws.getRow(r).height = height;
    return r;
  };

  // Brand band with the org name (right-aligned so it clears the logo on the left).
  merge(name, { bold: true, size: 16, color: { argb: 'FFFFFFFF' } }, hex, 46, hasLogo ? 'right' : 'left');
  // Detail lines — only the ones that actually have content (no empty grey bands).
  lines.forEach((ln, i) => merge(
    ln,
    { size: 9, color: { argb: 'FF475569' } },
    'F1F5F9', 16, 'left',
    i === lines.length - 1 ? { bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } } } : undefined,
  ));
  // Report title (with a brand-coloured underline accent).
  merge(reportTitle || '', { bold: true, size: 13, color: { argb: 'FF1E293B' } }, 'FFFFFF', 24, 'left',
    { bottom: { style: 'thin', color: { argb: 'FF' + hex } } });
  // Meta (generated date / totals).
  merge(metaText || '', { size: 9, color: { argb: 'FF64748B' } }, 'FFFFFF', 16);
  // Spacer.
  r += 1; ws.getRow(r).height = 8;

  // Logo floats over the brand band, vertically centred so it never spills into the
  // detail row below.
  const img = excelLogo(branding);
  if (img) {
    try {
      const id = wb.addImage({ base64: img.base64, extension: img.extension });
      const box = logoBox(img, 34, 150);
      ws.addImage(id, { tl: { col: 0.12, row: 0.16 }, ext: { width: box.width, height: box.height }, editAs: 'oneCell' });
    } catch { /* image errors must never break the export */ }
  }

  return r + 1; // column-header row
}

/**
 * Draw the branded header bar at the top of a jsPDF document. Returns the Y (mm)
 * below the header where body content should start.
 */
export function renderPdfHeader(doc, branding, { reportTitle, subtitle } = {}) {
  const pw = doc.internal.pageSize.getWidth();
  const { r, g, b } = brandRgb(branding);
  const name = (branding && branding.name) || 'Rostery';
  const lines = brandingLines(branding);

  // Brand bar.
  const barH = 24;
  const midY = barH / 2 + 1.6;   // visual vertical centre for bar text
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pw, barH, 'F');

  // Logo (left) — vertically centred in the bar.
  let textX = 14;
  const logo = pdfLogo(branding);
  if (logo) {
    try {
      const maxH = 16, maxW = 50;
      let h = maxH, w = logo.w && logo.h ? (logo.w / logo.h) * h : maxW;
      if (w > maxW) { w = maxW; h = logo.w && logo.h ? (logo.h / logo.w) * w : maxH; }
      doc.addImage(logo.dataUrl, logo.format, 14, (barH - h) / 2, w, h);
      textX = 14 + w + 6;
    } catch { /* ignore */ }
  }

  // Org name (white, vertically centred on the bar).
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(name, textX, midY);

  // Generated date (right, on the bar).
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  let d = '';
  try { d = new Date().toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { d = new Date().toISOString(); }
  doc.text(`Generated: ${d}`, pw - 14, midY, { align: 'right' });

  // Title block under the bar.
  let y = barH + 9;
  if (reportTitle) {
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(reportTitle, 14, y);
    y += 5.5;
  }
  // Org letterhead detail line(s) — muted, under the title.
  if (lines.length) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    for (const ln of lines) { doc.text(ln.slice(0, 190), 14, y); y += 4.2; }
  }
  if (subtitle) {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 130, 145);
    doc.text(subtitle, 14, y);
    y += 4.5;
  }
  // Brand-coloured divider before the table.
  y += 1.5;
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.5);
  doc.line(14, y, pw - 14, y);
  return y + 4;
}
